import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy,Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';

import { AdminComponentBase } from 'app/layout/content';
import { PageSimpleContentComponent } from 'app/layout/content';
import { AuthorizationContext, AuthorizationService } from 'app/shared/authentication';
import { ErrorComponent } from 'app/shared/components/error.component';
import { ConfirmDialogService } from 'app/shared/dialog/confirm-dialog-service';
import { PendingProjectExpirationConfig, ProjectExpirationConfig, Subscription, SubscriptionPermission } from 'app/shared/graph';

import { SubscriptionsService } from './subscriptions.service';
import {
    UpdateSubscriptionProjectExpirationsDialogComponent,
    UpdateSubscriptionProjectExpirationsDialogData,
    UpdateSubscriptionProjectExpirationsDialogResult
} from './update-subscription-project-expirations-dialog.component';

@Component({
    selector: 'admin-subscription-cleanup-config',
    templateUrl: './subscription-cleanup-config-page.component.html',
    styleUrls: ['./subscription-cleanup-config-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, MatButton, MatIcon, ErrorComponent, DatePipe]
})
export class SubscriptionCleanupConfigPageComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly subscriptionsService = inject(SubscriptionsService);
    private readonly authorizationService = inject(AuthorizationService);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly dialog = inject(MatDialog);

    private subscriptionId: any;

    subscriptionWithCleanup: Subscription;
    authContext: AuthorizationContext;
    currentProjectExpirations: ProjectExpirationConfig | undefined;
    pendingProjectExpirations: PendingProjectExpirationConfig | undefined;

    ngOnInit(): void {
        this.authorizationService.authorizationContext().subscribe((authContext) => {
            this.authContext = authContext;
        });

        this.registerSubscription(
            this.route.params.subscribe(params => {
                this.subscriptionId = params.subscriptionId;
                this.load();
            }));
    }

    load() {
        this.subscribeWithGenericLoadingErrorHandling(this.subscriptionsService.loadWithCleanup(this.subscriptionId), (subscriptionWithCleanup) => {
            this.subscriptionWithCleanup = subscriptionWithCleanup;
            this.currentProjectExpirations = this.subscriptionWithCleanup.cleanup.projectExpirations || {};
            this.pendingProjectExpirations = this.subscriptionWithCleanup.cleanup.pendingProjectExpirations || {};
        });
    }

    ngOnDestroy() {
        this.unsubscribeSubscriptions();
    }

    cancelPendingProjectExpiration() {
        this.confirmDialogService
            .confirm(
                'Confirmation',
                'Do you want to cancel this configuration request?'
            )
            .subscribe(() => {
                this.subscribeWithGenericSavingErrorHandling(
                    this.subscriptionsService.cancelSubscriptionPendingProjectExpirations({
                        subscriptionId: this.subscriptionWithCleanup.id
                    }),
                    () => {
                        this.load();
                    });
            });
    }

    editExpirations() {
        let dialogRef = this.dialog.open<UpdateSubscriptionProjectExpirationsDialogComponent, UpdateSubscriptionProjectExpirationsDialogData, UpdateSubscriptionProjectExpirationsDialogResult>(
            UpdateSubscriptionProjectExpirationsDialogComponent,
            {
                data: <UpdateSubscriptionProjectExpirationsDialogData>{
                    photobookCleanupDelayInDays: this.currentProjectExpirations?.photobookDelayInDays,
                    giftingCleanupDelayInDays: this.currentProjectExpirations?.giftingDelayInDays,
                    printCleanupDelayInDays: this.currentProjectExpirations?.printDelayInDays,
                    giftingNotificationDelayInDays: this.currentProjectExpirations?.giftingNotificationDelayInDays,
                    photobookNotificationDelayInDays: this.currentProjectExpirations?.photobookNotificationDelayInDays,
                    printNotificationDelayInDays: this.currentProjectExpirations?.printNotificationDelayInDays
                }
            }
        );
        dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }
            this.subscribeWithGenericSavingErrorHandling(this.subscriptionsService.updateSubscriptionCleanupDelay({
                        ...result,
                        subscriptionId: this.subscriptionWithCleanup.id,
                    }
                ),
                () => {
                    this.load();
                }
            );
        });
    }

    canManageSubscription(): boolean {
        return this.authContext.hasSubscriptionPermissions(this.subscriptionWithCleanup.id, SubscriptionPermission.ManageSubscriptionMetadata);
    }
}
